import { gsap } from 'gsap';



import './style.scss';

// Base URL (works for both / and /preview/ thanks to Vite --base)
const BASE = import.meta.env.BASE_URL;

// Set --logo-bg for the topbar logo (SCSS uses url() which needs the correct base path)
document.documentElement.style.setProperty('--logo-bg', `url(${BASE}auralyt.svg)`);

/* ========= Development Environment & Logging ========= */

// Check if we're in development mode
const IS_DEV = import.meta.env.VITE_IS_DEV === 'true';

// Conditional logging functions
const devLog = (...args) => { if (IS_DEV) console.log(...args); };
const devWarn = (...args) => { if (IS_DEV) console.warn(...args); };
const devError = (...args) => { if (IS_DEV) console.error(...args); };
const devInfo = (...args) => { if (IS_DEV) console.info(...args); };

/* ========= Turnstile loader (non-blocking) ========= */

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let turnstileLoadPromise = null;

function loadTurnstileScript({ timeoutMs = 8000 } = {}) {
  if (window.turnstile) return Promise.resolve(true);
  if (turnstileLoadPromise) return turnstileLoadPromise;

  turnstileLoadPromise = new Promise((resolve) => {
    let settled = false;
    const settle = (ok) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };

    const onLoad = () => settle(!!window.turnstile);
    const onError = () => {
      devWarn('Turnstile script failed to load');
      settle(false);
    };

    const timeoutId = setTimeout(() => {
      devWarn('Turnstile script load timed out');
      settle(false);
    }, timeoutMs);

    const cleanupAndSettle = (ok) => {
      clearTimeout(timeoutId);
      settle(ok);
    };

    const existing = document.querySelector('script[data-auralyt-turnstile="1"]');
    if (existing) {
      if (window.turnstile) {
        cleanupAndSettle(true);
        return;
      }
      existing.addEventListener('load', () => cleanupAndSettle(!!window.turnstile), { once: true });
      existing.addEventListener('error', () => cleanupAndSettle(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-auralyt-turnstile', '1');
    script.addEventListener('load', () => {
      onLoad();
      clearTimeout(timeoutId);
    }, { once: true });
    script.addEventListener('error', () => {
      onError();
      clearTimeout(timeoutId);
    }, { once: true });
    document.head.appendChild(script);
  });

  return turnstileLoadPromise;
}

/* ========= Text splitting & highlighting utils ========= */

/** Split a paragraph <p> into sentence spans with class 'sentence' */
function splitParagraphIntoSentences(pEl) {
  // Simple sentence splitter: ., !, ? followed by space/newline or end.
  const text = pEl.innerHTML;
  // Preserve existing inline tags (like <b>) by splitting on text content boundaries:
  // Strategy: temporarily replace <b>…</b> with markers, split, then restore.
  const placeholders = [];
  const protectedHtml = text.replace(/<b[^>]*>.*?<\/b>/gi, (m) => {
    const token = `__BOLD_${placeholders.length}__`;
    placeholders.push(m);
    return token;
  });
  
  const parts = protectedHtml
  .split(/([.!?]+)(\s+|$)/) // keep delimiters
  .reduce((acc, cur, i, arr) => {
    if (!cur) return acc;
    // group into sentence + delimiter
    if (acc.pending == null) {
      acc.pending = cur;
    } else {
      acc.pending += cur;
      acc.sentences.push(acc.pending.trim());
      acc.pending = null;
    }
    return acc;
  }, { sentences: [], pending: null }).sentences;
  
  // Restore bold placeholders and wrap each sentence
  pEl.innerHTML = parts.map(s => {
    let restored = s;
    placeholders.forEach((html, idx) => {
      restored = restored.replaceAll(`__BOLD_${idx}__`, html);
    });
    return `<span class="sentence">${restored}</span>`;
  }).join(' ');
}

/** Wrap configured keywords with <span class="pulse-term">…</span> */
function wrapKeywords(rootEl, keywords = []) {
  if (!keywords.length) return;
  // Build a regex that preserves HTML tags: act only on text nodes.
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (!n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (n.parentElement && n.parentElement.closest('.pulse-term, .sentence')) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  
  const ranges = [];
  let node;
  const rx = new RegExp(`\\b(${keywords.map(k => escapeRegExp(k)).join('|')})\\b`, 'gi');
  
  while ((node = walker.nextNode())) {
    const text = node.nodeValue;
    let m, lastIndex = 0, out = '';
    let matched = false;
    while ((m = rx.exec(text)) !== null) {
      matched = true;
      out += text.slice(lastIndex, m.index) + `<span class="pulse-term">${m[0]}</span>`;
      lastIndex = m.index + m[0].length;
    }
    if (matched) {
      out += text.slice(lastIndex);
      const span = document.createElement('span');
      span.innerHTML = out;
      ranges.push({ node, replacement: span });
    }
  }
  
  ranges.forEach(({ node, replacement }) => {
    node.parentNode.replaceChild(replacement, node);
  });
  
  function escapeRegExp(s){ return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
}

/** Animate one content slide on entry */
function animateContentSlide(slideEl, { keywords = [], speed = 1 } = {}) {
  const paras = Array.from(slideEl.querySelectorAll('p'));
  if (!paras.length) return;
  
  // 1) Mark paragraphs for animation & prepare micro-parallax offsets
  paras.forEach((p, idx) => {
    p.classList.add('anim-paragraph');
    // tiny alternating offsets for micro-parallax
    const offset = (idx % 2 === 0 ? 1 : -1) * 6; // px
    gsap.set(p, { y: offset, opacity: 0 });
  });
  
  // 2) First paragraph → sentence-by-sentence
  const first = paras[0];
  if (first) {
    // split only once
    if (!first.dataset.splitDone) {
      splitParagraphIntoSentences(first);
      first.dataset.splitDone = '1';
    }
  }
  
  // 3) Keyword wrapping (in all paragraphs, including split sentences)
  if (keywords.length) wrapKeywords(slideEl, keywords);
  
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) {
    gsap.to(paras, { opacity: 1, y: 0, duration: 0.25 });
    return;
  }
  
  // 4) Build timeline
  const tl = gsap.timeline({ defaults: { ease: 'power2.out' }});
  
  // first paragraph sentences
  if (first) {
    const sentences = first.querySelectorAll('.sentence');
    tl.to(sentences, {
      opacity: 1,
      y: 0,
      duration: 0.5 * speed,
      stagger: 0.08 * speed
    }, 0);
  }
  
  // remaining paragraphs (including first as a group “settle”)
  tl.to(paras, {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    duration: 0.6 * speed,
    stagger: 0.12 * speed
  }, 0.1);
  
  // return timeline for optional chaining
  return tl;
}


// ===== BACKGROUND GRADIENT (CSS variable driven) =====
function setGradientAngle() {
  const now = new Date();
  const seconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const angle = (seconds / 86400) * 360; // full rotation every 24h
  const angle2 = 45 + (seconds / 86400) * 360; // full rotation every 24h
  document.documentElement.style.setProperty('--bg-angle', `${angle}deg`);
  document.documentElement.style.setProperty('--bg-angle2', `${angle2}deg`);
}

function animateGradientAngle() {
  setGradientAngle();
  requestAnimationFrame(animateGradientAngle);
}

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (prefersReduced) {
  setGradientAngle();
  setInterval(setGradientAngle, 60 * 1000); // update once per minute
} else {
  animateGradientAngle();
}

// helpers intro 

/* ---------- SVG helpers ---------- */
async function loadInlineSVG(url, target) {
  const res = await fetch(url, { cache: 'no-cache' });
  const text = await res.text();
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  svg.removeAttribute('width'); svg.removeAttribute('height');
  target.innerHTML = '';
  target.appendChild(svg);
  return svg;
}

/** Clona i fill in stroke “dash-draw” e nasconde i fill per fade-in successivo */
function prepareStrokeDraw(svg, {
  fillSelector = 'path, polygon',
  strokeClass = 'stroke-draw',
  strokeColor = '#fff',
  strokeWidth = 2.0,
  place = 'over', // 'under' o 'over'
} = {}) {
  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('class', strokeClass);
  if (place === 'under' && svg.firstElementChild) svg.insertBefore(g, svg.firstElementChild);
  else svg.appendChild(g);
  
  const fills = Array.from(svg.querySelectorAll(fillSelector));
  const strokes = [];
  
  fills.forEach(el => {
    if (!(el instanceof SVGGeometryElement)) return;
    const clone = el.cloneNode(true);
    clone.setAttribute('fill', 'none');
    clone.setAttribute('stroke', strokeColor);
    clone.setAttribute('stroke-width', strokeWidth);
    clone.setAttribute('stroke-linecap', 'round');
    clone.setAttribute('stroke-linejoin', 'round');
    let L = 1000; try { L = el.getTotalLength(); } catch {}
    clone.style.strokeDasharray = L;
    clone.style.strokeDashoffset = L;
    g.appendChild(clone);
    strokes.push(clone);
  });
  
  fills.forEach(el => { el.style.opacity = 0; });
  return { strokes, fills };
}

window.addEventListener('auralyt:languageToggle', (ev) => {
  // puoi ignorare ev.detail se non serve: usiamo lo stato locale
  const next = language === 'EN' ? 'IT' : 'EN';
  setLanguage(next);
});

document.addEventListener('DOMContentLoaded', () => {
  document.documentElement.setAttribute('lang', language.toLowerCase());
  loadSlides(language).catch(devError);
});




/* =================== Build slides from JSON =================== */
const slidesEl = document.getElementById('slides');
const minimapEl = document.querySelector('.minimap');

const SCROLL_TYPES_MINIMAP = new Set(['intro','hub','content','contacts','cta']); // solo queste compaiono a pallini

let language = localStorage.getItem('auralyt:lang') || 'EN'; // default EN

function slidesUrlFor(lang) {
  return lang.toUpperCase() === 'IT' ? `${BASE}slides-it.json` : `${BASE}slides-en.json`;
}

function setLanguage(lang) {
  const next = (lang || '').toUpperCase();
  if (!next || (next !== 'EN' && next !== 'IT')) return;
  if (next === language) return;
  
  language = next;
  localStorage.setItem('auralyt:lang', language);
  // Aggiorna attributo lang del documento (accessibilità/SEO)
  document.documentElement.setAttribute('lang', language.toLowerCase());
  
  // Ricarica tutto
  loadSlides(language).catch(devError);
}

async function loadSlides(lang = language) {
  const url = slidesUrlFor(lang);
  const res = await fetch(url, { cache: 'no-cache' });
  const data = await res.json();
  
  // Pulisci i container principali
  slidesEl.innerHTML = '';
  minimapEl.innerHTML = '';
  
  renderSlides(data.slides);
  setupCarousels();
  setupMinimap(data.slides);
  setupQuickNav(data.quickNav);
  setupQuickNavMicroFX();      // microinterazioni quick-nav
  setupScrollAnimations();     // animazioni scroll + intro logo
  setupLogoNavigation();       // click on logo to go back to intro
  setupHubNavigation();        // hub card + cta-row clicks
  
  // Aggiorna il bottone lingua (faccia front/back corretta)
  syncLanguageToggleVisual(lang);
}

function setupFooter(footer) {
  const footerEl = document.querySelector('footer.site-footer');
  const copyrightEl = footerEl.querySelector('.footer-copy');
  copyrightEl.textContent = footer.copy || '';
  const companyEl = footerEl.querySelector('.footer-company');
  companyEl.innerHTML = footer.company + '<br>' + footer.address + '<br>' + footer.vat || '';
  
}

function syncLanguageToggleVisual(lang = language) {
  const btn = document.querySelector('.quick-nav .qn-btn.lang-toggle .label');
  if (!btn) return;
  const front = btn.querySelector('.face.front');
  const back  = btn.querySelector('.face.back');
  if (!front || !back) return;
  
  // Front = lingua alternativa (quella verso cui si può switchare), Back = lingua corrente
  if (lang.toUpperCase() === 'IT') {
    front.textContent = 'EN';  // Mostra EN quando la lingua corrente è IT
    back.textContent  = 'IT';
  } else {
    front.textContent = 'IT';  // Mostra IT quando la lingua corrente è EN
    back.textContent  = 'EN';
  }
}


/* ============== Symmetric Gradient Overlay ============== */
function hexToRgba(hex, alpha) {
  const clean = hex.replace(/^#/, '');
  let r, g, b;
  if (clean.length === 3) {
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
  } else {
    r = parseInt(clean.slice(0, 2), 16);
    g = parseInt(clean.slice(2, 4), 16);
    b = parseInt(clean.slice(4, 6), 16);
  }
  return `rgba(${r},${g},${b},${alpha})`;
}

function makeSymmetricGradient(bg) {
  const color = bg.overlayColor || '#18143c';
  const clearWidth = parseFloat(bg.overlayClearWidth);
  // Validate: must be a number between 0 and 100
  const validClear = (typeof clearWidth === 'number' && !isNaN(clearWidth) && clearWidth >= 0 && clearWidth <= 100)
    ? clearWidth : 60; // default 60

  // Special cases
  if (validClear === 0) {
    // Entirely opaque
    return `linear-gradient(90deg, ${hexToRgba(color, 1)} 0%, ${hexToRgba(color, 1)} 100%)`;
  }
  if (validClear === 100) {
    // Entirely transparent
    return `linear-gradient(90deg, ${hexToRgba(color, 0)} 0%, ${hexToRgba(color, 0)} 100%)`;
  }

  const edgeWidth = (100 - validClear) / 2;
  return `linear-gradient(90deg,
    ${hexToRgba(color, 1)} 0%,
    ${hexToRgba(color, 0)} ${edgeWidth}%,
    ${hexToRgba(color, 0)} ${100 - edgeWidth}%,
    ${hexToRgba(color, 1)} 100%)`;
}


function renderSlides(slides) {
  slidesEl.innerHTML = '';
  for (const s of slides) {

    //don't render a "display":false slide
    if (s.display === false) continue;

    if (s.type === 'footer') {
      const footer = document.createElement('footer');
      footer.className = 'site-footer'; // Just footer, not a slide
      footer.id = s.id;
      footer.innerHTML = `
        <div class="footer-left">
          <span class="footer-copy">${s.copy || ''}</span>
        </div>
        <div class="footer-right">
          <span class="footer-company">${s.company || ''}${s.address ? ' | ' + s.address : ''}${s.vat ? ' | ' + s.vat : ''}</span>
          
        </div>
      `;
      slidesEl.appendChild(footer);
      continue;
    }

    const section = document.createElement('section');
    section.className = `slide ${s.type}`;
    section.id = s.id;
    
    // Backgrounds
    if (s.bg?.kind === 'photo') {
      // Photo wrapper — clipping container
      const wrapper = document.createElement('div');
      wrapper.className = 'bg-photo-wrapper';

      // Offset layer — translates the image + overlay as a single unit
      const offsetLayer = document.createElement('div');
      offsetLayer.className = 'bg-photo-offset';

      // Shared image/overlay container — dimensions match the rendered image
      const container = document.createElement('div');
      container.className = 'bg-photo-container';

      const img = document.createElement('img');
      img.className = 'bg-photo'; img.loading = 'lazy'; img.decoding = 'async'; img.src = BASE + s.bg.src.replace(/^\//, '');
      container.appendChild(img);

      // Symmetric gradient overlay — inside the container, same dimensions as the image
      const ov = document.createElement('div');
      ov.className = 'bg-photo-overlay';
      ov.style.background = makeSymmetricGradient(s.bg);
      container.appendChild(ov);

      offsetLayer.appendChild(container);

      // Apply photo offset translation if configured
      if (s.bg.photoOffsetX) {
        offsetLayer.style.transform = `translateX(${s.bg.photoOffsetX})`;
      }

      // Apply left clip if configured — on the wrapper, so it stays independent of the image offset
      // clip-path: inset(top right bottom left) — using photoClipLeft to clip from left edge
      if (s.bg.photoClipLeft) {
        wrapper.style.clipPath = `inset(0 0 0 ${s.bg.photoClipLeft})`;
      }

      wrapper.appendChild(offsetLayer);
      section.appendChild(wrapper);

      const credits = document.createElement('div');
      credits.className = 'bg-credits';
      credits.innerHTML = s.bg.credits || '';
      section.appendChild(credits);
    } else if (s.bg?.kind === 'solid') {
      section.style.background = s.bg.color || '#0e1020';
    } else if (s.bg?.kind === 'gradient' || s.type === 'intro' || s.type === 'hub') {
      const g = document.createElement('div');
      g.className = 'bg-gradient';
      if (s.bg?.from && s.bg?.to) {
        g.style.background = `linear-gradient(var(--bg-angle), ${s.bg.from}, ${s.bg.to})`;
      }
      section.appendChild(g);
    }
    
    // Content
    const inner = document.createElement('div');
    inner.className = 'slide-inner';
    
    if (s.type === 'intro') {
      inner.innerHTML = `
        <div class="logo-effect" aria-hidden="true"></div>
      `;
    } else if (s.type === 'hub') {
      // Hub / Product selection screen
      inner.innerHTML = `
        <div class="hub-content">
          <div class="hub-corporate-card" data-goto="${s.auralyt?.target || 'about-us'}">
            ${s.auralyt?.eyebrow ? `<div class="hub-eyebrow hub-eyebrow-corp">${s.auralyt.eyebrow}</div>` : ''}
            <h2 class="hub-corp-headline">${s.auralyt?.headline || ''}</h2>
            <p class="hub-corp-desc">${s.auralyt?.description || ''}</p>
            <span class="hub-cta hub-cta-corp">${s.auralyt?.cta || ''}</span>
          </div>
          <div class="hub-products">
            ${(s.products||[]).map(p => {
              const isExternal = !p.internal;
              const eyecolor = p.id === 'howididit-card' ? 'hub-eyebrow-hidi' : 'hub-eyebrow-product';
              const logoHtml = p.logo ? `<img class="hub-product-logo" src="${BASE}${p.logo.replace(/^\//, '')}" alt="${p.logoAlt || p.name || ''}" loading="lazy">` : '';
              return `<div class="hub-product-card ${p.id}" data-target="${p.target}" data-external="${isExternal}">
                ${logoHtml}
                <div class="hub-eyebrow ${eyecolor}">${p.name}</div>
                <h3 class="hub-product-headline">${p.headline || ''}</h3>
                <p class="hub-product-desc">${p.description || ''}</p>
                <span class="hub-cta hub-cta-product">${p.cta || ''} ${isExternal ? '↗' : '→'}</span>
              </div>`;
            }).join('')}
          </div>
        </div>
      `;
    } else if (s.type === 'content') {
      const isTwoCol = s.layout === 'two-col';
      let layoutClass = isTwoCol ? ' layout-two-col' : '';
      let mainHtml = '';
      let hasCtaRow = false;

      (s.content||[]).forEach((c, idx) => {
        if (c.type === 'paragraph') {
          mainHtml += `<p>${c.text}</p>`;
        } else if(c.type === 'heading') {
          mainHtml += `<h2>${c.text}</h2>`;
        } else if(c.type === 'image') {
          mainHtml += `<img src="${c.url}" alt="${c.alt}" style="width:${c.width || 'auto'};">`;
        } else if(c.type === 'box-container') {
          const classes = ['box-container'];
          const boxCount = (c.boxes||[]).length;
          if (boxCount <= 2) classes.push('grid-2');
          else if (boxCount <= 4) classes.push('grid-4');
          else classes.push('grid-2');
          classes.push('w100');
          const styleAttr = c.style ? ` style="${c.style}"` : '';
          const innerBoxes = (c.boxes||[]).map(b => {
            if (b.type === 'box') {
              const title = b.title ? `<h3>${b.title}</h3>` : '';
              const text  = b.text ? `<p>${b.text}</p>` : '';
              const logo = b.logo ? `<img class="box-logo" src="${BASE}${b.logo.replace(/^\//, '')}" alt="${b.logoAlt || ''}" loading="lazy">` : '';
              return `<div class="box">${logo}${title}${text}</div>`;
            } else if (b.type === 'carousel') {
              const items = (b.items||[]).map(it => {
                if (it.type === 'image') {
                  return `<div class="item"><img src="${it.url}" alt="${it.alt || ''}" style="width:${it.width || '100%'};"></div>`;
                }
                return '';
              }).join('');
              return `<div class="carousel" data-auto="true"><div class="track">${items}</div></div>`;
            }
            return '';
          }).join('');
          mainHtml += `<div class="${classes.join(' ')}"${styleAttr}>${innerBoxes}</div>`;
        } else if(c.type === 'cta-row') {
          hasCtaRow = true;
          const buttons = (c.buttons||[]).map(b => {
            if (b.external) {
              return `<a href="${b.href}" target="_blank" rel="noopener noreferrer" class="hub-cta${b.primary ? ' cta-primary' : ' cta-secondary'}">${b.label} ↗</a>`;
            } else {
              return `<a href="#" class="hub-cta${b.primary ? ' cta-primary' : ' cta-secondary'}" data-goto="${b.target || 'demo'}">${b.label} →</a>`;
            }
          }).join('');
          mainHtml += `<div class="cta-row">${buttons}</div>`;
        }
      });

      if (isTwoCol) {
        // Two-col: left column has text content, right has visual steps
        const leftCol = document.createElement('div');
        leftCol.className = 'tc-left';
        leftCol.innerHTML = mainHtml;

        // Handle CTA on content slides (if not already in content)
        if (s.cta && !hasCtaRow) {
          const ctaEl = document.createElement('div');
          ctaEl.className = 'slide-cta';
          ctaEl.innerHTML = `<a href="#" class="hub-cta cta-primary" data-goto="${s.cta.target}">${s.cta.label} →</a>`;
          leftCol.appendChild(ctaEl);
        }

        inner.appendChild(leftCol);

        // Right column: visual steps
        if (s.visual?.steps) {
          const stepsHtml = s.visual.steps.map(st => 
            `<div class="step-item"><span class="step-num">${st.num}</span><span class="step-label">${st.label}</span></div>`
          ).join('');
          const rightCol = document.createElement('div');
          rightCol.className = 'visual-steps';
          rightCol.innerHTML = stepsHtml;
          inner.appendChild(rightCol);
        }
      } else {
        inner.innerHTML = mainHtml;

        // Handle CTA on content slides
        if (s.cta && !hasCtaRow) {
          const ctaEl = document.createElement('div');
          ctaEl.className = 'slide-cta';
          ctaEl.innerHTML = `<a href="#" class="hub-cta cta-primary" data-goto="${s.cta.target}">${s.cta.label} →</a>`;
          inner.appendChild(ctaEl);
        }

        // Visual steps (if any on non-two-col slides - can wrap below)
        if (s.visual?.steps) {
          const stepsHtml = s.visual.steps.map(st => 
            `<div class="step-item"><span class="step-num">${st.num}</span><span class="step-label">${st.label}</span></div>`
          ).join('');
          const stepsEl = document.createElement('div');
          stepsEl.className = 'visual-steps';
          stepsEl.innerHTML = stepsHtml;
          inner.appendChild(stepsEl);
        }
      }

      if (layoutClass) inner.className += layoutClass;
    } else if (s.type === 'contacts') {
      inner.innerHTML = makeContactFormHTML(s);
      // call after rendering the contacts slide - defer to ensure DOM is ready
      setTimeout(() => setupContactForm(), 0);
      
    } else if (s.type === 'cta') {
      inner.innerHTML = `
      <h2>${s.title || 'Prenota demo'}</h2>
      <div id="calendly-embed" style="min-width:320px;width:80vw;height:40vh;"></div>
      `;
      
      setTimeout(() => {
        // Load Calendly script dynamically only when needed
        if (!window.Calendly) {
          const script = document.createElement('script');
          script.src = 'https://assets.calendly.com/assets/external/widget.js';
          script.onload = () => {
            Calendly.initInlineWidget({
              url: 'https://calendly.com/gianandrea-auralyt',
              parentElement: document.getElementById('calendly-embed'),
              resize: true
            });
          };
          document.head.appendChild(script);
        } else {
          Calendly.initInlineWidget({
            url: 'https://calendly.com/gianandrea-auralyt',
            parentElement: document.getElementById('calendly-embed'),
            resize: true
          });
        }
      }, 0);
    } 
      
      section.appendChild(inner);
      slidesEl.appendChild(section);
    }
  }


 function setupContactForm() {

  devLog('Starting setupContactForm');

  const form = document.getElementById('contact-form');
  if (!form) {
    devLog('Contact form not found');
    return;

  }

  // messages - they are siblings to the form, not children
  const okMsg  = form.parentElement.querySelector('p.form-success');
  const errMsg = form.parentElement.querySelector('p.form-error');
  
  // console.log('Message elements found:', { okMsg: !!okMsg, errMsg: !!errMsg });

  // time-trap (passive)
  let ft = form.querySelector('input[name="ft"]');
  if (!ft) {
    ft = document.createElement('input');
    ft.type = 'hidden';
    ft.name = 'ft';
    form.appendChild(ft);
    ft.value = String(Date.now());
    devLog('Created hidden ft field with value:', ft.value);
  } else {
    ft.value = String(Date.now());
    devLog('Reset hidden ft field to value:', ft.value);
  }
  
  // Verify ft field is accessible
  setTimeout(() => {
    const ftCheck = form.querySelector('input[name="ft"]');
    devLog('FT field verification:', { 
      found: !!ftCheck, 
      value: ftCheck?.value,
      inDOM: document.contains(ftCheck)
    });
  }, 100);

  // honeypot (hidden, avoid autofill)
  let hp = form.querySelector('input[name="website"]');
  if (!hp) {
    hp = document.createElement('input');
    hp.type = 'text';
    hp.name = 'website';
    hp.autocomplete = 'off';
    hp.tabIndex = -1;
    hp.setAttribute('aria-hidden', 'true');
    hp.style.cssText = 'position:absolute;left:-9999px;opacity:0;height:0;width:0;';
    form.appendChild(hp);
  }

  const show = (el) => {
    // console.log('Attempting to show element:', el);
    if (el) {
      el.classList.add('visible');
      // console.log('Element shown, classes:', el.classList.toString());
    }
  };
  const hide = (el) => {
    if (el) {
      el.classList.remove('visible');
      // console.log('Element hidden, classes:', el.classList.toString());
    }
  };

  async function onSubmit(e) {
    devLog('Form submission started');
    e.preventDefault();               // <-- prevents GET navigation
    hide(okMsg); hide(errMsg);
    
    // Debug form data immediately
    const debugFd = new FormData(form);
    devLog('All form data:', Array.from(debugFd.entries()));

    // passive traps
    const elapsed = Date.now() - Number(ft.value || 0);
    const honeypotValue = (hp.value || '').trim();
    
    devLog('Anti-spam checks:', { 
      honeypot: honeypotValue, 
      honeypotLength: honeypotValue.length,
      elapsed: elapsed,
      ftValue: ft.value,
      currentTime: Date.now(),
      honeypotFailed: honeypotValue !== '',
      timeFailed: elapsed < 2000
    });
    
    if (honeypotValue !== '' || elapsed < 2000) {
      devLog('Anti-spam check failed - showing error');
      devLog('Error message element:', errMsg);
      show(errMsg);
      return;
    }
    
    devLog('Anti-spam checks passed - proceeding with submission');

    // get Turnstile token (Invisible)
    let token = '';

    if (!window.turnstile) {
      devLog('Turnstile not available at submit, trying lazy load');
      const loaded = await loadTurnstileScript({ timeoutMs: 5000 });
      if (loaded) initializeTurnstileWidget(form);
    }

    devLog('Checking Turnstile:', { hasTurnstile: !!window.turnstile });
    
    if (window.turnstile) {
      try {
        // Simple approach: just proceed without waiting for ready callback
        // since the script should be loaded synchronously now
        
        const widgetEl = form.querySelector('.cf-turnstile');
        devLog('Turnstile widget element:', widgetEl);
        devLog('Widget sitekey:', widgetEl?.dataset?.sitekey);
        devLog('Widget innerHTML:', widgetEl?.innerHTML);
        
        // For invisible CAPTCHA, get the token (should be available automatically)
        token = window.turnstile.getResponse(widgetEl) || widgetEl?.dataset?.turnstileToken;
        devLog('Turnstile token check:', token ? 'FOUND' : 'NOT_FOUND');
        
        // If no token, wait a moment and try again (invisible CAPTCHA might still be processing)
        if (!token) {
          devLog('No token found, waiting for invisible CAPTCHA to complete...');
          
          // Wait up to 3 seconds for the invisible CAPTCHA to complete
          for (let i = 0; i < 30; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
            token = window.turnstile.getResponse(widgetEl) || widgetEl?.dataset?.turnstileToken;
            if (token) {
              devLog('Token received after waiting');
              break;
            }
          }
        }
        
        devLog('Final token status:', token ? 'READY TO SUBMIT' : 'STILL MISSING');
        
        // If no response, the widget might need user interaction or there's an issue
        if (!token) {
          devLog('No token available after execution - this could indicate a captcha challenge');
          // For development, use bypass. For production, this is an error.
          if (location.hostname === 'localhost' || location.hostname.includes('.local')) {
            devLog('Development environment - using bypass token');
            token = 'dev-bypass-token';
          } else {
            // In production, if invisible captcha has no token after execution, it's likely failed
            devLog('Production environment - captcha failed, showing error message');
            show(errMsg);
            return;
          }
        } else {
          devLog('Got valid captcha token:', token.substring(0, 20) + '...');
        }
      } catch (err) {
        devLog('Turnstile error:', err);
        if (location.hostname === 'localhost' || location.hostname.includes('.local')) {
          devLog('Development environment - using bypass token');
          token = 'dev-bypass-token';
        } else {
          devLog('Production environment - captcha error, showing error message');
          show(errMsg);
          return;
        }
      }
    } else {
      devLog('Turnstile not available');
      if (location.hostname === 'localhost' || location.hostname.includes('.local')) {
        devLog('Development environment - using bypass token');
        token = 'dev-bypass-token';
      } else {
        devLog('Production environment - no captcha available, showing error');
        show(errMsg);
        return;
      }
    }

    // send via AJAX
    const fd = new FormData(form);
    if (token) fd.set('cf-turnstile-response', token);
    
    devLog('Making POST request to:', form.action || '/api/contact.php');
    devLog('FormData entries:', Array.from(fd.entries()));

    try {
      const res = await fetch(form.action || '/api/contact.php', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' }
      });
      devLog('Response received:', { status: res.status, ok: res.ok });
      
      const data = await res.json().catch(() => ({}));
      devLog('Response data:', data);
      
      if (!res.ok || !data.ok) throw new Error(data.reason || 'server');

      devLog('Success - showing success message');
      show(okMsg);
      form.reset();
      ft.value = String(Date.now());     // reset time-trap
      
      // Only reset widget if it exists and was found earlier
      if (window.turnstile) {
        try {
          const widgetEl = form.querySelector('.cf-turnstile');
          if (widgetEl) {
            window.turnstile.reset(widgetEl);
          }
        } catch (err) {
          // console.log('Widget reset error (non-critical):', err.message);
        }
      }
    } catch (err) {
      devError('Form submission failed:', err);
      devError('Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack
      });
      show(errMsg);
    }
  }

  // avoid duplicate binds on HMR/re-render
  form.removeEventListener('submit', onSubmit);
  form.addEventListener('submit', onSubmit);
  
  devLog('Event listener attached to form:', form.id);

  // Load Turnstile script asynchronously so it cannot block page rendering.
  loadTurnstileScript()
    .then((loaded) => {
      if (loaded) initializeTurnstileWidget(form);
      else devWarn('Turnstile unavailable: form submit will show error in production');
    })
    .catch((err) => devError('Unexpected Turnstile loader error:', err));

} //setupContactForm

function initializeTurnstileWidget(form) {
  const widgetEl = form.querySelector('.cf-turnstile');
  if (!widgetEl) {
    devLog('No Turnstile widget element found');
    return;
  }

  // Only try to render if Turnstile is available
  if (window.turnstile && !widgetEl.dataset.widgetId) {
    try {
      devLog('Initializing invisible Turnstile widget on form setup...');
      const widgetId = window.turnstile.render(widgetEl, {
        sitekey: widgetEl.dataset.sitekey,
        size: 'invisible',
        theme: 'light',
        action: 'contact',
        callback: function(token) {
          devLog('Invisible Turnstile completed automatically, token received:', token ? 'YES' : 'NO');
          // Store the token for later use
          widgetEl.dataset.turnstileToken = token;
        },
        'error-callback': function() {
          devError('Turnstile widget error');
        }
      });
      
      widgetEl.dataset.widgetId = widgetId;
      devLog('Turnstile widget initialized with ID:', widgetId);
      
      // For invisible CAPTCHA, check if token is received after initialization
      setTimeout(() => {
        const token = window.turnstile.getResponse(widgetEl) || widgetEl.dataset.turnstileToken;
        devLog('Invisible CAPTCHA status after initialization:', token ? 'TOKEN_READY' : 'STILL_PROCESSING');
      }, 2000);
      
    } catch (error) {
      devError('Failed to initialize Turnstile widget:', error);
    }
  } else if (!window.turnstile) {
    devLog('Turnstile not available during form setup');
  } else {
    devLog('Turnstile widget already initialized');
  }
}




function makeContactFormHTML(s) {
  const apiBase = import.meta.env.VITE_API_BASE || '/api';
  let html =  `
    <h2>${s.title || 'Contatti'}</h2>
    <form id="contact-form" class="form-grid contact-form" method="post" action="${apiBase}/contact.php" autocomplete="off">`

    html += s.description ? `<p class="form-description">${s.description}</p>` : '';

    s.fields.forEach(f => {
      switch(f.type) {
        case 'text':
          html += `
            <label for="${f.name}">${f.label || 'Name'}</label>
            <input type="text" name="${f.name}"  id="${f.name}"  placeholder="${f.placeholder || ''}" required>
          `;
          break;
        case 'email':
          html += `
            <label for="${f.name}">${f.label || 'Email'}</label>
            <input type="email" name="${f.name}"  id="${f.name}"  placeholder="${f.placeholder || ''}" required>
          `;
          break;
        case 'textarea':
          html += `
            <label for="${f.name}">${f.label || 'Message'}</label>
            <textarea name="${f.name}"  id="${f.name}"  placeholder="${f.placeholder || ''}" rows="6" required></textarea>
          `;
          break;
        case 'submit':
          html += `
            <button type="submit">${f.label || 'Submit'}</button>
          `;
      }
    });

    html += `<input type="text" name="website" autocomplete="off"
         style="position:absolute;left:-9999px;opacity:0;height:0;width:0;" aria-hidden="true" tabindex="-1">

  <input type="hidden" name="ft" value="">

  <div class="cf-turnstile"
       data-sitekey="${import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAB4_Gwf8iCP2MVLV'}"
       data-size="invisible"
       data-theme="light"
       data-action="contact">
   </div>`;

      html += `</form>`;

      // success message

      html += `<p class="form-success">${s.successMessage || 'Messaggio inviato con successo!'}</p>`;
      html += `<p class="form-error">${s.errorMessage || 'Si è verificato un errore durante l\'invio del messaggio.'}</p>`;

  return html;

}


  /* ================== Minimap (pallini laterali) ================== */
  function setupMinimap(slides) {
    minimapEl.innerHTML = '';
    const scrollers = slides.filter(s => SCROLL_TYPES_MINIMAP.has(s.type));
    scrollers.forEach((s, idx) => {
      const b = document.createElement('button');
      b.setAttribute('title', s.title || s.id);
      if (idx === 0) b.classList.add('active');
      b.addEventListener('click', () => navigateTo(s.id));
      minimapEl.appendChild(b);
    });
    
    // Evidenziazione del pallino corrente con IntersectionObserver
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
      .filter(e => e.isIntersecting)
      .sort((a,b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const id = visible.target.id;
      const index = scrollers.findIndex(s => s.id === id);
      [...minimapEl.children].forEach((el,i)=> el.classList.toggle('active', i===index));
    }, { root: slidesEl, threshold: [0.6] });
    
    scrollers.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
  }
  
  /* ===================== Quick Nav (header) ====================== */

  function setupQuickNav(quickNav = []) {
    const container = document.querySelector('.quick-nav');
    container.innerHTML = '';
    quickNav.forEach(item => {
      const btn = document.createElement('button')
      btn.textContent = item.label;
      btn.dataset.goto = item.target;
      btn.classList.add('qn-btn');
      // if (item.target === 'en') btn.classList.add('lang-toggle');
      const tgt = String(item.target || '').toLowerCase();
      if (tgt === 'en' || tgt === 'it' || tgt === 'lang' || tgt === 'language') {
        btn.classList.add('lang-toggle');
      }
      container.appendChild(btn);
    });
    document.querySelectorAll('.quick-nav [data-goto]').forEach(btn => {
      if (!btn.classList.contains('lang-toggle')) {
        btn.addEventListener('click', () => {
          const target = btn.dataset.goto;
          if (target.startsWith('http')) {
            window.open(target, '_blank', 'noopener,noreferrer');
          } else {
            navigateTo(target);
          }
        });
      }
    });
  }
  
  /* ===================== Logo Navigation (header) ====================== */
  function setupLogoNavigation() {
    const logo = document.querySelector('.topbar .brand h1.logo');
    if (!logo) {
      devLog('Logo not found for navigation setup');
      return;
    }
    
    // Add click listener to navigate back to intro
    logo.addEventListener('click', () => {
      devLog('Logo clicked - navigating to intro');
      navigateTo('intro');
    });
    
    // Add cursor pointer to indicate it's clickable
    logo.style.cursor = 'pointer';
    
    devLog('Logo navigation setup complete');
  }
  
  /* ===================== Hub & CTA row navigation ====================== */
  function setupHubNavigation() {
    // Hub corporate card click
    document.querySelectorAll('.hub-corporate-card').forEach(card => {
      card.addEventListener('click', () => {
        const target = card.dataset.goto;
        if (target) navigateTo(target);
      });
    });
    
    // Hub product cards
    document.querySelectorAll('.hub-product-card').forEach(card => {
      card.addEventListener('click', () => {
        const target = card.dataset.target;
        const isExternal = card.dataset.external === 'true';
        if (isExternal) {
          window.open(target, '_blank', 'noopener,noreferrer');
        } else {
          navigateTo(target);
        }
      });
    });
    
    // CTA row buttons (data-goto on <a>)
    document.querySelectorAll('.cta-row a[data-goto]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        navigateTo(btn.dataset.goto);
      });
    });

    // Slide CTA (single CTA property on content slides)
    document.querySelectorAll('.slide-cta a[data-goto]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        navigateTo(btn.dataset.goto);
      });
    });
  }
  
  function setupQuickNavMicroFX() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const btns = document.querySelectorAll('.quick-nav .qn-btn');
    
    // Ensure expected structure
    btns.forEach(btn => {
      if (!btn.querySelector('.label')) {
        const spanLabel = document.createElement('span');
        spanLabel.className = 'label';
        spanLabel.textContent = btn.textContent;
        btn.textContent = '';
        btn.appendChild(spanLabel);
      }
      if (btn.classList.contains('lang-toggle')) {
        const lab = btn.querySelector('.label');
        if (!lab.querySelector('.face')) {
          const front = document.createElement('span');
          const back  = document.createElement('span');
          front.className = 'face front';
          back.className  = 'face back';
          front.textContent = 'EN';
          back.textContent  = 'IT';
          lab.innerHTML = '';
          lab.append(front, back);
        }
      }
    });
    
    if (reduce) return;
    
    // ---------- Magnetic Drift (all buttons) ----------
    // btns.forEach(btn => {
      if (!reduce) btns.forEach(btn => {
      if (btn.classList.contains('lang-toggle')) return; // lang has its own hover; still keep tiny drift? comment to re-enable
      const label = btn.querySelector('.label');
      const setT = gsap.quickTo(label, 'x', { duration: 0.25, ease: 'power3.out' });
      const setY = gsap.quickTo(label, 'y', { duration: 0.25, ease: 'power3.out' });
      const setSh = gsap.quickTo(btn,   'boxShadow', { duration: 0.3, ease: 'power2.out' });
      
      function onMove(e) {
        const r = btn.getBoundingClientRect();
        const cx = r.left + r.width/2;
        const cy = r.top  + r.height/2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        // normalize drift (cap at 6px)
        const max = 6;
        const x = gsap.utils.clamp(-max, max, dx * 0.08);
        const y = gsap.utils.clamp(-max, max, dy * 0.10);
        setT(x); setY(y);
        const dist = Math.min(1, Math.hypot(x,y)/max);
        const glow = `0 0 ${8+10*dist}px rgba(106,40,255,${0.22+0.18*dist}), 0 0 ${18+22*dist}px rgba(255,97,200,${0.16+0.14*dist})`;
        setSh(glow);
      }
      function onLeave() {
        gsap.to(label, { x: 0, y: 0, duration: 0.35, ease: 'power3.out' });
        gsap.to(btn,   { boxShadow: '0 0 0 rgba(0,0,0,0)', duration: 0.35, ease: 'power2.out' });
      }
      
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      btn.addEventListener('touchend', onLeave, { passive: true });
    });
    
    // ---------- Language Flip (hover 3D flip, click optional) ----------
    const lang = document.querySelector('.quick-nav .qn-btn.lang-toggle');
    if (lang) {
      const label = lang.querySelector('.label');
      // // hover flip
      // lang.addEventListener('mouseenter', () => {
        //   gsap.to(label, { rotateY: 180, duration: 0.6, ease: 'power3.inOut' });
      //   gsap.to(lang,  { boxShadow: '0 0 18px rgba(106,40,255,0.35)', duration: 0.4, ease: 'power2.out' });
      // });
      // lang.addEventListener('mouseleave', () => {
        //   gsap.to(label, { rotateY: 0,   duration: 0.6, ease: 'power3.inOut' });
      //   gsap.to(lang,  { boxShadow: '0 0 0 rgba(0,0,0,0)', duration: 0.35, ease: 'power2.out' });
      // });
      
      if (!reduce) {
        lang.addEventListener('mouseenter', () => {
          gsap.to(label, { rotateY: 180, duration: 0.6, ease: 'power3.inOut' });
          gsap.to(lang,  { boxShadow: '0 0 18px rgba(106,40,255,0.35)', duration: 0.4, ease: 'power2.out' });
        });
        lang.addEventListener('mouseleave', () => {
          gsap.to(label, { rotateY: 0,   duration: 0.6, ease: 'power3.inOut' });
          gsap.to(lang,  { boxShadow: '0 0 0 rgba(0,0,0,0)', duration: 0.35, ease: 'power2.out' });
        });
      }
      
      // (Optional) On click: brief lock to alternate face, then emit event for your locale switcher
      lang.addEventListener('click', () => {
        // visual tap
        gsap.fromTo(lang, { scale: 0.98 }, { scale: 1, duration: 0.18, ease: 'power2.out' });
        // dispatch event you can hook to actually change language
        window.dispatchEvent(new CustomEvent('auralyt:languageToggle', { detail: { target: lang.dataset.goto || 'en' }}));
      });
    }
  }
  
  function navigateTo(id) {
    const target = document.getElementById(id);
    if (!target) return;
    
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    const scroller = slidesEl;
    
    if (isMobile) {
      // Mobile-specific navigation with scroll-snap handling
      devLog('Mobile navigation to:', id);
      
      // Temporarily disable scroll-snap to prevent conflicts
      const originalScrollSnapType = scroller.style.scrollSnapType;
      scroller.style.scrollSnapType = 'none';
      
      // Use direct scroll calculation instead of scrollIntoView
      const scrollTop = target.offsetTop;
      
      gsap.to(target, { opacity: 0.6, duration: 0.2, onComplete: () => {
        // Smooth scroll to target position
        gsap.to(scroller, {
          scrollTop: scrollTop,
          duration: 0.8,
          ease: 'power2.out',
          onComplete: () => {
            // Re-enable scroll-snap after navigation
            setTimeout(() => {
              scroller.style.scrollSnapType = originalScrollSnapType || 'y mandatory';
              gsap.to(target, { opacity: 1, duration: 0.5 });
            }, 100);
          }
        });
      }});
    } else {
      // Desktop navigation (original behavior)
      gsap.to(target, { opacity: 0.6, duration: 0.2, onComplete: () => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        gsap.to(target, { opacity: 1, duration: 0.5, delay: 0.25 });
      }});
    }
  }
  
  /* ============== Scroll animations (intro + contenuto) ============== */
  /* Fade/translate/blur per passaggi intro -> intro1 -> intro2 -> intro3 */
  function setupScrollAnimations() {
    const items = slidesEl.querySelectorAll('.slide.content, .slide.hub, .slide.intro');
    items.forEach((el, i) => {
      // stato iniziale
      el.style.willChange = 'transform, opacity, filter';
      gsap.set(el, { opacity: 0.0, y: 30, filter: 'blur(15px)' });
      // on-enter viewport (70% visibile)
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            gsap.to(el, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power2.out' });
          } else {
            // leggero fade quando esce
            gsap.to(el, { opacity: 0.6, y: 10, filter: 'blur(10px)', duration: 0.5, ease: 'power1.out' });
          }
        });
      }, { root: slidesEl, threshold: 0.7 });
      io.observe(el);
    });
    
    // ========= Intro: inietta i 3 SVG e lancia timeline unica =========
    const intro = document.getElementById('intro');
    if (intro) {
      const logoEff = intro.querySelector('.logo-effect');
      if (logoEff) {
        // stage layout (non serve CSS aggiuntivo, lo facciamo inline qui)
        Object.assign(logoEff.style, {
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          gap: 'clamp(16px, 3vw, 36px)',
          width: 'min(50vw, 1200px)',
          position:'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
        });
        
        const symbolWrap   = document.createElement('div');
        const wordcol      = document.createElement('div');
        const wordmarkWrap = document.createElement('div');
        const payoffWrap   = document.createElement('div');
        
        Object.assign(symbolWrap.style,   { width: 'min(10vw, 360px)' });
        Object.assign(wordcol.style,      { display: 'grid', gap: 'clamp(8px,1.6vw,16px)' });
        Object.assign(wordmarkWrap.style, { width: 'min(36vw, 820px)' });
        Object.assign(payoffWrap.style,   { width: 'min(24vw, 560px)' });
        
        wordcol.appendChild(wordmarkWrap);
        wordcol.appendChild(payoffWrap);
        logoEff.appendChild(symbolWrap);
        logoEff.appendChild(wordcol);
        
        let introPlayed = false;
        
        const playIntroTimeline = async () => {
          if (introPlayed) return;
          introPlayed = true;
          
          // carica gli SVG
          const [symbolSVG, wordmarkSVG, payoffSVG] = await Promise.all([
            loadInlineSVG(`${BASE}auralyt_symbol.svg`,   symbolWrap),
            loadInlineSVG(`${BASE}auralyt_wordmark.svg`, wordmarkWrap),
            loadInlineSVG(`${BASE}auralyt_payoff.svg`,   payoffWrap),
          ]);
          
          // Hook classi nel simbolo
          const rings   = symbolSVG.querySelectorAll('.ring');
          const radials = symbolSVG.querySelectorAll('.rad');
          const circs   = symbolSVG.querySelectorAll('.circ');
          const dotGrp  = symbolSVG.querySelector('#dot') || symbolSVG;
          
          // stati iniziali
          gsap.set([symbolSVG, wordmarkSVG, payoffSVG], { opacity: 1 });
          if (rings.length) gsap.set(rings,   { opacity: 0, scale: 0.9, transformOrigin: '50% 50%' });
          if (radials.length) gsap.set(radials, { opacity: 0, scale: 0,   transformOrigin: '50% 50%' });
          if (circs.length) gsap.set(circs,   { opacity: 0, scale: 0,   transformOrigin: '50% 50%' });
          if (dotGrp) gsap.set(dotGrp,  { opacity: 0, scale: 0.8, transformOrigin: '50% 50%' });
          
          // stroke draw per wordmark & payoff
          const { strokes: wmStrokes, fills: wmFills } = prepareStrokeDraw(wordmarkSVG, {
            fillSelector: 'path, polygon',
            strokeWidth: 2.2,
            place: 'over'
          });
          const { strokes: pfStrokes, fills: pfFills } = prepareStrokeDraw(payoffSVG, {
            fillSelector: 'path, polygon',
            strokeWidth: 2.0,
            place: 'over'
          });
          
          if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const elements = [];
            if (rings.length) elements.push(...rings);
            if (radials.length) elements.push(...radials);
            if (circs.length) elements.push(...circs);
            if (dotGrp) elements.push(dotGrp);
            if (elements.length) gsap.set(elements, { opacity: 1, scale: 1 });
            
            const strokes = [...wmStrokes, ...pfStrokes];
            const fills = [...wmFills, ...pfFills];
            if (strokes.length) gsap.set(strokes, { strokeDashoffset: 0 });
            if (fills.length) gsap.set(fills, { opacity: 1 });
            return;
          }
          
          // timeline unica
          const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
          
          // Simbolo + Wordmark insieme
          if (rings.length) tl.to(rings,   { opacity: 1, scale: 1, duration: 0.45, stagger: 0.06 }, 0.00);
          if (radials.length) tl.to(radials, { opacity: 1, scale: 1, duration: 0.35, stagger: 0.04 }, 0.10);
          if (circs.length) tl.to(circs,   { opacity: 1, scale: 1, duration: 0.30, stagger: 0.05 }, 0.25);
          if (dotGrp) tl.to(dotGrp,  { opacity: 1, scale: 1, duration: 0.40 }, 0.35);
          if (dotGrp) tl.to(dotGrp,  { scale: 1.12, duration: 0.22, yoyo: true, repeat: 1, ease: 'power1.inOut' }, 0.55);
          tl.add(() => window.dispatchEvent(new CustomEvent('auralyt:pulse')), 0.55);
          
          
          if (wmStrokes.length) tl.to(wmStrokes, { strokeDashoffset: 0, duration: 1.05, stagger: 0.02 }, 0.00);
          if (wmFills.length) tl.to(wmFills,   { opacity: 1,        duration: 0.6,  stagger: 0.01 },   0.55);
          
          // Payoff dopo che il wordmark è leggibile
          if (pfStrokes.length) tl.to(pfStrokes, { strokeDashoffset: 0, duration: 0.9,  stagger: 0.015 }, '>-0.15');
          if (pfFills.length) tl.to(pfFills,   { opacity: 1,        duration: 0.55, stagger: 0.01  },  '<+0.35');
          
          tl.timeScale(0.25); // rallenta un po’
          
          // loop “radar” lentissimo del simbolo
          gsap.to(symbolSVG, { rotation: 360, duration: 36, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
        };
        
        // Avvia quando l’intro è ben visibile (o subito se prima slide)
        const ioIntro = new IntersectionObserver((entries) => {
          entries.forEach(e => { if (e.isIntersecting) playIntroTimeline().catch(devError); });
        }, { root: slidesEl, threshold: 0.6 });
        
        ioIntro.observe(intro);
      }
    }
    
    // Light up .pulse-term whenever the symbol dot pulses
    window.addEventListener('auralyt:pulse', () => {
      const terms = document.querySelectorAll('.pulse-term');
      terms.forEach(el => {
        el.classList.add('pulsing');
        setTimeout(() => el.classList.remove('pulsing'), 240);
      });
    });
    
    
    // Trigger text animation when content slide becomes visible
    const contentSlides = slidesEl.querySelectorAll('.slide.content');
    const KEYWORDS = [
      'AI', 'AI agentica', 'modelli di ragionamento',
      'dati sintetici', 'Auralyt'
    ];
    
    const ioText = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        // Prevent re-running
        if (el.dataset.textAnimated === '1') return;
        el.dataset.textAnimated = '1';
        animateContentSlide(el, { keywords: KEYWORDS, speed: 5.0 }); // speed>1 = slower
      });
    }, { root: slidesEl, threshold: 0.6 });
    
    contentSlides.forEach(el => ioText.observe(el));
    
      // === Mostra/nasconde .brand in base alla visibilità della slide intro ===
  const topbarBrand = document.querySelector('.topbar .brand');
  const introSection = document.getElementById('intro');

  if (topbarBrand && introSection) {
    const ioBrand = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          // siamo sulla slide intro → nascondi il logo in topbar
          gsap.to(topbarBrand, { opacity: 0, y: -10, duration: 0.4, ease: 'power2.out', pointerEvents: 'none' });
        } else {
          // stiamo scrollando oltre → mostra di nuovo
          gsap.to(topbarBrand, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', pointerEvents: 'auto' });
        }
      });
    }, { root: slidesEl, threshold: 0.5 });

    ioBrand.observe(introSection);
  }

  // === Mobile overlay animation during scroll ===
  setupMobileOverlayScrollAnimation();
    
  }

  /* ============== Mobile Overlay Scroll Animation ============== */
  function setupMobileOverlayScrollAnimation() {
    // Only apply on mobile devices
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    if (!isMobile) return;

    const overlays = document.querySelectorAll('.bg-photo-overlay');
    if (!overlays.length) return;

    let isScrolling = false;
    let scrollTimer = null;
    let animationTween = null;

    // Function to animate overlay opacity
    const animateOverlayOpacity = (targetOpacity, duration = 0.3) => {
      // Kill any existing animation
      if (animationTween) {
        animationTween.kill();
      }

      // Animate all overlays to target opacity
      animationTween = gsap.to(overlays, {
        opacity: targetOpacity,
        duration: duration,
        ease: 'power2.out'
      });
    };

    // Scroll start handler
    const handleScrollStart = () => {
      if (!isScrolling) {
        isScrolling = true;
        devLog('Scroll started - reducing overlay opacity');
        animateOverlayOpacity(0.20, 0.2); // Faster animation to reduce opacity
      }
    };

    // Scroll end handler
    const handleScrollEnd = () => {
      isScrolling = false;
      devLog('Scroll ended - restoring overlay opacity');
      animateOverlayOpacity(0.25, 0.4); // Slightly slower animation to restore opacity
    };

    // Scroll event listener with debouncing
    const handleScroll = () => {
      handleScrollStart();
      
      // Clear existing timer
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      
      // Set new timer for scroll end
      scrollTimer = setTimeout(handleScrollEnd, 150); // 150ms delay after scroll stops
    };

    // Add scroll listener to slides container
    slidesEl.addEventListener('scroll', handleScroll, { passive: true });

    devLog('Mobile overlay scroll animation initialized');
  }
  
  /* ============== Simple Auto Carousel ============== */
  function setupCarousels() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('.carousel[data-auto="true"]').forEach(carousel => {
      if (carousel.dataset.bound === '1') return; // avoid duplicates on HMR
      carousel.dataset.bound = '1';
      const track = carousel.querySelector('.track');
      if (!track) return;
      const items = Array.from(track.children);
      if (items.length <= 1 || reduce) return;
      let index = 0;
      const total = items.length;
      const go = (next) => {
        index = (next + total) % total;
        gsap.to(track, { xPercent: -100 * index, duration: 0.7, ease: 'power2.inOut' });
      };
      const timer = setInterval(() => go(index + 1), 3000);
      carousel.dataset.timer = String(timer);
    });
  }
  
